import { useState, useMemo } from "react";
import { Check, Plus, X, Search, Filter } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";

// Teeth layout - FDI notation
const UPPER_RIGHT = ["18", "17", "16", "15", "14", "13", "12", "11"];
const UPPER_LEFT = ["21", "22", "23", "24", "25", "26", "27", "28"];
const LOWER_LEFT = ["31", "32", "33", "34", "35", "36", "37", "38"];
const LOWER_RIGHT = ["48", "47", "46", "45", "44", "43", "42", "41"];

// Deciduous (baby) teeth — only rendered as small chips above/below the
// corresponding adult teeth. They mirror the adult layout for positions
// 11-15 / 21-25 / 41-45 / 31-35 (no baby teeth exist for molars 16-18 /
// 26-28 / 46-48 / 36-38, those slots are rendered as empty spacers so
// the rows stay aligned).
const UPPER_RIGHT_BABY = [null, null, null, "55", "54", "53", "52", "51"];
const UPPER_LEFT_BABY = ["61", "62", "63", "64", "65", null, null, null];
const LOWER_LEFT_BABY = ["71", "72", "73", "74", "75", null, null, null];
const LOWER_RIGHT_BABY = [null, null, null, "85", "84", "83", "82", "81"];

const UPPER_TEETH = [...UPPER_RIGHT, ...UPPER_LEFT];
const LOWER_TEETH = [...LOWER_LEFT, ...LOWER_RIGHT];

// Filtered (non-null) deciduous lists, used to extend arch/semi-arch
// selections so that selecting "Arcata Sup" / "Emiarcata Sup. Dx" / etc.
// also turns the corresponding baby teeth blue.
const UPPER_RIGHT_BABY_IDS = UPPER_RIGHT_BABY.filter(Boolean);
const UPPER_LEFT_BABY_IDS = UPPER_LEFT_BABY.filter(Boolean);
const LOWER_LEFT_BABY_IDS = LOWER_LEFT_BABY.filter(Boolean);
const LOWER_RIGHT_BABY_IDS = LOWER_RIGHT_BABY.filter(Boolean);

const UPPER_RIGHT_FULL = [...UPPER_RIGHT, ...UPPER_RIGHT_BABY_IDS];
const UPPER_LEFT_FULL = [...UPPER_LEFT, ...UPPER_LEFT_BABY_IDS];
const LOWER_LEFT_FULL = [...LOWER_LEFT, ...LOWER_LEFT_BABY_IDS];
const LOWER_RIGHT_FULL = [...LOWER_RIGHT, ...LOWER_RIGHT_BABY_IDS];
const UPPER_TEETH_FULL = [...UPPER_TEETH, ...UPPER_RIGHT_BABY_IDS, ...UPPER_LEFT_BABY_IDS];
const LOWER_TEETH_FULL = [...LOWER_TEETH, ...LOWER_LEFT_BABY_IDS, ...LOWER_RIGHT_BABY_IDS];
const ALL_TEETH = [...UPPER_TEETH, ...LOWER_TEETH];
// "Tutta la bocca" includes deciduous teeth too — same selection style
// as Arcata Sup/Inf which highlight permanent + baby teeth together.
const ALL_TEETH_WITH_BABY = [
  ...ALL_TEETH,
  ...UPPER_RIGHT_BABY_IDS,
  ...UPPER_LEFT_BABY_IDS,
  ...LOWER_LEFT_BABY_IDS,
  ...LOWER_RIGHT_BABY_IDS,
];

// ---- Arch pricing helpers ----

const getEffectiveMultiplier = (denti) => {
  if (!denti || denti.length === 0) return 1;
  const s = new Set(denti);
  let multiplier = 0;
  const accounted = new Set();

  // Full arches first. Both "Arcata Superiore" (16 teeth) and
  // "Arcata Inferiore" (16 teeth) — on their own or together —
  // are a single treatment unit (multiplier 1). Baby teeth of the same
  // arch are absorbed into the same unit so the "unitario" count stays 1.
  const fullUp = UPPER_TEETH.every((t) => s.has(t));
  const fullLo = LOWER_TEETH.every((t) => s.has(t));
  if (fullUp || fullLo) {
    multiplier += 1;
    if (fullUp) {
      UPPER_TEETH.forEach((t) => accounted.add(t));
      [...UPPER_RIGHT_BABY_IDS, ...UPPER_LEFT_BABY_IDS].forEach((t) => {
        if (s.has(t)) accounted.add(t);
      });
    }
    if (fullLo) {
      LOWER_TEETH.forEach((t) => accounted.add(t));
      [...LOWER_LEFT_BABY_IDS, ...LOWER_RIGHT_BABY_IDS].forEach((t) => {
        if (s.has(t)) accounted.add(t);
      });
    }
  }

  // Semi-arches for remaining teeth (also absorbing the baby teeth of
  // the same quadrant).
  const semis = [
    [UPPER_RIGHT, UPPER_RIGHT_BABY_IDS],
    [UPPER_LEFT, UPPER_LEFT_BABY_IDS],
    [LOWER_RIGHT, LOWER_RIGHT_BABY_IDS],
    [LOWER_LEFT, LOWER_LEFT_BABY_IDS],
  ];
  for (const [semi, baby] of semis) {
    if (semi.every((t) => s.has(t) && !accounted.has(t))) {
      multiplier += 1;
      semi.forEach((t) => accounted.add(t));
      baby.forEach((t) => { if (s.has(t)) accounted.add(t); });
    }
  }

  // Remaining individual teeth (incl. stray baby teeth without the
  // matching permanent quadrant)
  const remaining = [...s].filter((t) => !accounted.has(t));
  multiplier += remaining.length;

  return Math.max(multiplier, 1);
};

const getArcataInfo = (denti) => {
  if (!denti || denti.length === 0) return { label: null, qtyLabel: null };
  const s = new Set(denti);
  const parts = [];
  const accounted = new Set();

  const fullUp = UPPER_TEETH.every((t) => s.has(t));
  const fullLo = LOWER_TEETH.every((t) => s.has(t));

  if (fullUp) {
    parts.push("Arcata Superiore");
    UPPER_TEETH.forEach((t) => accounted.add(t));
    // Any baby teeth present in the upper arch are part of the same
    // arch-level selection — don't list them separately.
    [...UPPER_RIGHT_BABY_IDS, ...UPPER_LEFT_BABY_IDS].forEach((t) => {
      if (s.has(t)) accounted.add(t);
    });
  }
  if (fullLo) {
    parts.push("Arcata Inferiore");
    LOWER_TEETH.forEach((t) => accounted.add(t));
    [...LOWER_LEFT_BABY_IDS, ...LOWER_RIGHT_BABY_IDS].forEach((t) => {
      if (s.has(t)) accounted.add(t);
    });
  }

  const semiDefs = [
    { teeth: UPPER_RIGHT, baby: UPPER_RIGHT_BABY_IDS, name: "Emiarcata Sup. Dx" },
    { teeth: UPPER_LEFT, baby: UPPER_LEFT_BABY_IDS, name: "Emiarcata Sup. Sx" },
    { teeth: LOWER_RIGHT, baby: LOWER_RIGHT_BABY_IDS, name: "Emiarcata Inf. Dx" },
    { teeth: LOWER_LEFT, baby: LOWER_LEFT_BABY_IDS, name: "Emiarcata Inf. Sx" },
  ];
  for (const { teeth, baby, name } of semiDefs) {
    if (teeth.every((t) => s.has(t) && !accounted.has(t))) {
      parts.push(name);
      teeth.forEach((t) => accounted.add(t));
      // Roll associated baby teeth (if selected) into the same group.
      baby.forEach((t) => { if (s.has(t)) accounted.add(t); });
    }
  }

  const remaining = [...s].filter((t) => !accounted.has(t));
  const multiplier = getEffectiveMultiplier(denti);

  if (parts.length > 0 && remaining.length === 0) {
    return { label: parts.join(" + "), qtyLabel: multiplier === 1 ? "Unitario" : `\u00d7${multiplier}` };
  }
  return { label: null, qtyLabel: `\u00d7${multiplier}` };
};

// ---- Small presentational components ----

const ToothButton = ({ number, isSelected, isLocked, onClick, treatmentCount }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`tooth-${number}`}
    className={`relative w-9 h-10 sm:w-11 sm:h-12 rounded-lg border-2 transition-all duration-200 flex flex-col items-center justify-center gap-0.5 ${
      isSelected
        ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-600/30 scale-105"
        : isLocked
        ? "bg-blue-100 border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-200 hover:scale-105 cursor-pointer"
        : "bg-white border-slate-200 text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:scale-105"
    }`}
  >
    <span className="text-[10px] sm:text-xs font-bold">{number}</span>
    {isSelected && <Check className="w-3 h-3" />}
    {treatmentCount > 0 && (
      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold border ${isSelected ? "bg-green-500 text-white border-blue-600" : "bg-green-500 text-white border-white"}`}>
        {treatmentCount}
      </div>
    )}
  </button>
);

const TeethRow = ({ teeth, selectedTeeth, lockedTeeth, onToothClick, teethTreatmentCounts }) => (
  <div className="flex gap-0.5 sm:gap-1 justify-center">
    {teeth.map((t) => (
      <ToothButton
        key={t}
        number={t}
        isSelected={selectedTeeth.includes(t)}
        isLocked={lockedTeeth.includes(t)}
        onClick={() => onToothClick(t)}
        treatmentCount={teethTreatmentCounts[t] || 0}
      />
    ))}
  </div>
);

// Deciduous (baby) tooth chip — rectangular with rounded corners,
// number ~2/3 the size of a permanent tooth. Uses the same colour
// palette so selection / lock / treatment-count visuals stay coherent.
const BabyToothButton = ({ number, isSelected, isLocked, onClick, treatmentCount }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`tooth-${number}`}
    className={`relative w-9 h-7 sm:w-11 sm:h-8 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${
      isSelected
        ? "bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-600/30 scale-105"
        : isLocked
        ? "bg-blue-100 border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-200 hover:scale-105 cursor-pointer"
        : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:bg-blue-50 hover:scale-105"
    }`}
  >
    <span className="text-[8px] sm:text-[9px] font-bold">{number}</span>
    {treatmentCount > 0 && (
      <div className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold border ${isSelected ? "bg-green-500 text-white border-blue-600" : "bg-green-500 text-white border-white"}`}>
        {treatmentCount}
      </div>
    )}
  </button>
);

// Row of deciduous tooth slots. `null` entries render as invisible
// spacers of the same width so that each baby tooth sits exactly above
// the permanent tooth it corresponds to.
const BabyTeethRow = ({ teeth, selectedTeeth, lockedTeeth, onToothClick, teethTreatmentCounts }) => (
  <div className="flex gap-0.5 sm:gap-1 justify-center">
    {teeth.map((t, idx) =>
      t ? (
        <BabyToothButton
          key={t}
          number={t}
          isSelected={selectedTeeth.includes(t)}
          isLocked={lockedTeeth.includes(t)}
          onClick={() => onToothClick(t)}
          treatmentCount={teethTreatmentCounts[t] || 0}
        />
      ) : (
        <div key={`spacer-${idx}`} className="w-9 h-7 sm:w-11 sm:h-8" aria-hidden="true" />
      )
    )}
  </div>
);

// ---- Dental Chart (visual tooth selector) ----

const QUICK_CODES = ["V", "ABT", "ABT2", "ABTV", "FLU", "OPT", "TEL", "IMS", "FOTO", "VISO", "VI", "CEFA"];

const DentalChart = ({ allSelectedTeeth, lockedTeeth, teethTreatmentCounts, onToothClick, isUpperArcataSelected, isLowerArcataSelected, hasUpperTeethSelected, hasLowerTeethSelected, onArcataToggle, treatments, onQuickTreatment, selectedQuickCodes }) => {
  const isAllSelected = (teeth) => teeth.every((t) => allSelectedTeeth.includes(t));
  const hasAnySelected = (teeth) => teeth.some((t) => allSelectedTeeth.includes(t));

  return (
  <div className="bg-transparent rounded-xl py-6 px-2 sm:py-10 sm:px-10 border border-slate-300">
    <div className="text-center mb-8">
      <h3 className="text-sm font-semibold text-slate-700 mb-1">Seleziona Denti</h3>
      <p className="text-xs text-slate-500">Clicca sui denti per selezionarli. I denti selezionati insieme avranno lo stesso trattamento.</p>
    </div>

    {/* === DESKTOP LAYOUT (hidden on mobile) === */}
    <div className="hidden md:block">
      {/* Upper teeth */}
      <div className="flex flex-col items-center gap-8 mb-7">
        <div className="flex items-center justify-center gap-8">
          <div className="flex items-center gap-1.5">
            <Checkbox id="upper-right-semi" checked={isAllSelected(UPPER_RIGHT)} onCheckedChange={() => onArcataToggle(UPPER_RIGHT_FULL, hasAnySelected(UPPER_RIGHT_FULL))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="upper-right-semi" className="text-[10px] font-medium text-slate-400 cursor-pointer whitespace-nowrap">Emiarcata Dx</label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="upper-arcata" checked={isUpperArcataSelected} onCheckedChange={() => onArcataToggle(UPPER_TEETH_FULL, hasUpperTeethSelected)} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="upper-arcata" className="text-xs font-medium text-slate-500 cursor-pointer">Arcata Superiore {isUpperArcataSelected && <span className="text-blue-600">(tutti)</span>}</label>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox id="upper-left-semi" checked={isAllSelected(UPPER_LEFT)} onCheckedChange={() => onArcataToggle(UPPER_LEFT, hasAnySelected(UPPER_LEFT))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="upper-left-semi" className="text-[10px] font-medium text-slate-400 cursor-pointer whitespace-nowrap">Emiarcata Sx</label>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <div className="flex gap-6">
            <BabyTeethRow teeth={UPPER_RIGHT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
            <div className="w-px bg-transparent self-stretch" />
            <BabyTeethRow teeth={UPPER_LEFT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
          </div>
          <div className="flex gap-6">
            <TeethRow teeth={UPPER_RIGHT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
            <div className="w-px bg-slate-200 self-stretch" />
            <TeethRow teeth={UPPER_LEFT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-2 my-7">
        <div className="flex-1 border-t border-dashed border-slate-300" />
        <span className="text-[10px] text-slate-400 font-medium">LINEA MEDIANA</span>
        <div className="flex-1 border-t border-dashed border-slate-300" />
      </div>

      {/* Lower teeth */}
      <div className="flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-1">
          <div className="flex gap-6">
            <TeethRow teeth={LOWER_RIGHT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
            <div className="w-px bg-slate-200 self-stretch" />
            <TeethRow teeth={LOWER_LEFT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
          </div>
          <div className="flex gap-6">
            <BabyTeethRow teeth={LOWER_RIGHT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
            <div className="w-px bg-transparent self-stretch" />
            <BabyTeethRow teeth={LOWER_LEFT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
          </div>
        </div>
        <div className="flex items-center justify-center gap-8">
          <div className="flex items-center gap-1.5">
            <Checkbox id="lower-right-semi" checked={isAllSelected(LOWER_RIGHT)} onCheckedChange={() => onArcataToggle(LOWER_RIGHT_FULL, hasAnySelected(LOWER_RIGHT_FULL))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="lower-right-semi" className="text-[10px] font-medium text-slate-400 cursor-pointer whitespace-nowrap">Emiarcata Dx</label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="lower-arcata" checked={isLowerArcataSelected} onCheckedChange={() => onArcataToggle(LOWER_TEETH_FULL, hasLowerTeethSelected)} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="lower-arcata" className="text-xs font-medium text-slate-500 cursor-pointer">Arcata Inferiore {isLowerArcataSelected && <span className="text-blue-600">(tutti)</span>}</label>
          </div>
          <div className="flex items-center gap-1.5">
            <Checkbox id="lower-left-semi" checked={isAllSelected(LOWER_LEFT)} onCheckedChange={() => onArcataToggle(LOWER_LEFT_FULL, hasAnySelected(LOWER_LEFT_FULL))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
            <label htmlFor="lower-left-semi" className="text-[10px] font-medium text-slate-400 cursor-pointer whitespace-nowrap">Emiarcata Sx</label>
          </div>
        </div>
      </div>
    </div>

    {/* === MOBILE LAYOUT (4 semi-arches in column, hidden on desktop) === */}
    <div className="md:hidden space-y-6">
      {/* Arcata Superiore checkbox */}
      <div className="flex items-center justify-center gap-2 py-2 border-y border-dashed border-slate-300">
        <Checkbox id="m-upper-arcata" checked={isUpperArcataSelected} onCheckedChange={() => onArcataToggle(UPPER_TEETH_FULL, hasUpperTeethSelected)} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
        <label htmlFor="m-upper-arcata" className="text-xs font-medium text-slate-500 cursor-pointer">Arcata Superiore {isUpperArcataSelected && <span className="text-blue-600">(tutti)</span>}</label>
      </div>

      {/* Emiarcata Sup Dx */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox id="m-upper-right-semi" checked={isAllSelected(UPPER_RIGHT)} onCheckedChange={() => onArcataToggle(UPPER_RIGHT_FULL, hasAnySelected(UPPER_RIGHT_FULL))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
          <label htmlFor="m-upper-right-semi" className="text-xs font-medium text-slate-500 cursor-pointer">Emiarcata Sup. Dx</label>
        </div>
        <BabyTeethRow teeth={UPPER_RIGHT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
        <TeethRow teeth={UPPER_RIGHT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
      </div>

      {/* Emiarcata Sup Sx */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox id="m-upper-left-semi" checked={isAllSelected(UPPER_LEFT)} onCheckedChange={() => onArcataToggle(UPPER_LEFT_FULL, hasAnySelected(UPPER_LEFT_FULL))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
          <label htmlFor="m-upper-left-semi" className="text-xs font-medium text-slate-500 cursor-pointer">Emiarcata Sup. Sx</label>
        </div>
        <BabyTeethRow teeth={UPPER_LEFT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
        <TeethRow teeth={UPPER_LEFT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-2">
        <div className="flex-1 border-t border-dashed border-slate-300" />
        <span className="text-[10px] text-slate-400 font-medium">LINEA MEDIANA</span>
        <div className="flex-1 border-t border-dashed border-slate-300" />
      </div>

      {/* Arcata Inferiore checkbox */}
      <div className="flex items-center justify-center gap-2 py-2 border-y border-dashed border-slate-300">
        <Checkbox id="m-lower-arcata" checked={isLowerArcataSelected} onCheckedChange={() => onArcataToggle(LOWER_TEETH_FULL, hasLowerTeethSelected)} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
        <label htmlFor="m-lower-arcata" className="text-xs font-medium text-slate-500 cursor-pointer">Arcata Inferiore {isLowerArcataSelected && <span className="text-blue-600">(tutti)</span>}</label>
      </div>

      {/* Emiarcata Inf Dx */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox id="m-lower-right-semi" checked={isAllSelected(LOWER_RIGHT)} onCheckedChange={() => onArcataToggle(LOWER_RIGHT, hasAnySelected(LOWER_RIGHT))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
          <label htmlFor="m-lower-right-semi" className="text-xs font-medium text-slate-500 cursor-pointer">Emiarcata Inf. Dx</label>
        </div>
        <TeethRow teeth={LOWER_RIGHT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
        <BabyTeethRow teeth={LOWER_RIGHT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
      </div>

      {/* Emiarcata Inf Sx */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox id="m-lower-left-semi" checked={isAllSelected(LOWER_LEFT)} onCheckedChange={() => onArcataToggle(LOWER_LEFT, hasAnySelected(LOWER_LEFT))} className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
          <label htmlFor="m-lower-left-semi" className="text-xs font-medium text-slate-500 cursor-pointer">Emiarcata Inf. Sx</label>
        </div>
        <TeethRow teeth={LOWER_LEFT} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
        <BabyTeethRow teeth={LOWER_LEFT_BABY} selectedTeeth={allSelectedTeeth} lockedTeeth={lockedTeeth} onToothClick={onToothClick} teethTreatmentCounts={teethTreatmentCounts} />
      </div>
    </div>

    {/* "Tutta la bocca" quick treatment buttons */}
    <div className="mt-4 pt-14 border-t border-slate-200 pb-4 space-y-6">
      <div className="text-center">
        <span className="text-sm font-medium text-slate-600">Tutta la bocca</span>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {QUICK_CODES.map((code) => {
          const t = treatments.find((tr) => tr.code === code);
          if (!t) return null;
          const isSelected = selectedQuickCodes.has(code);
          return (
            <button
              key={code}
              type="button"
              onClick={() => onQuickTreatment(code)}
              data-testid={`quick-${code}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors ${
                isSelected
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
              }`}
            >
              {code}
            </button>
          );
        })}
      </div>
    </div>
  </div>
  );
};

// ---- Treatment row (category filter + search) ----

const TrattamentoRow = ({ trattamento, onRemove, onUpdate, onSetIndirettoTarget, treatments, baseCosts, enableDualPriceUI, enableIndirettoDropdown, priceOverrideEnabled, fasdacPriceOverride, index, showRemove, numDenti, qtyLabel, categories }) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchOpenMobile, setSearchOpenMobile] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterOpenMobile, setFilterOpenMobile] = useState(false);
  const [activeCategory, setActiveCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchQueryMobile, setSearchQueryMobile] = useState("");
  const hasTreatment = !!trattamento.treatmentCode;
  const euroValue = trattamento.euro || 0;
  const totalEuro = hasTreatment ? euroValue * numDenti : 0;
  // QFO è un caso storico (Fasdac, prezzo annuale variabile inserito a
  // mano). L'editabilità "per tutte le prestazioni Indiretti" è gestita
  // più sotto via `isIndirettiEditable`.
  const isQfoLegacy = trattamento.treatmentCode === "QFO";
  const handlePriceChange = (raw) => {
    // Accetta solo cifre. Limite di sicurezza max 5 (=€99999) per evitare
    // typo da 6+ cifre. Vuoto = 0. NB: l'edit manuale viene tracciato
    // come `priceChoice="manual"` così le statistiche non lo confondono
    // con prezzo Assic. o BDD del listino.
    const limit = isQfoLegacy ? 4 : 5;
    const digits = (raw || "").replace(/\D/g, "").slice(0, limit);
    const value = digits === "" ? 0 : Number(digits);
    onUpdate(trattamento.treatmentCode, trattamento.treatmentName, value, "manual");
  };

  const filteredTreatments = useMemo(
    () => (activeCategory ? treatments.filter((t) => (t.category || "").toLowerCase() === activeCategory.toLowerCase()) : treatments),
    [activeCategory, treatments]
  );

  const sortedTreatments = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return filteredTreatments;
    const matching = filteredTreatments.filter((t) => t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q));
    return matching.sort((a, b) => {
      const aStarts = a.code.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.code.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts;
    });
  }, [filteredTreatments, searchQuery]);

  const sortedTreatmentsMobile = useMemo(() => {
    const q = searchQueryMobile.toLowerCase().trim();
    if (!q) return filteredTreatments;
    const matching = filteredTreatments.filter((t) => t.code.toLowerCase().includes(q) || t.name.toLowerCase().includes(q));
    return matching.sort((a, b) => {
      const aStarts = a.code.toLowerCase().startsWith(q) ? 0 : 1;
      const bStarts = b.code.toLowerCase().startsWith(q) ? 0 : 1;
      return aStarts - bStarts;
    });
  }, [filteredTreatments, searchQueryMobile]);

  // Helper: per i codici "Indiretto comparabili" (ABF/ABO/ABP/ABR/ABT/
  // ABT2/ABTV) restituisce il prezzo BDD dal `baseCosts` overlay quando
  // disponibile (cioè quando il reparto attivo è "Indiretti" e
  // l'assicurazione selezionata ha quel codice). Tornando `null` indica
  // che il doppio prezzo non va mostrato.
  const _baseCostFor = (code) => {
    if (!baseCosts) return null;
    const c = String(code || "").toUpperCase();
    if (!(c in baseCosts)) return null;
    const v = baseCosts[c];
    return typeof v === "number" ? v : null;
  };

  // Doppio prezzo per la riga del trattamento già scelto.
  // Calcolato da:
  //   - assicCost = prezzo assicurazione del codice corrente (dalla lista
  //     `treatments`, che varia in base al foglio Google selezionato).
  //   - bddCost  = prezzo BDD per lo stesso codice (dall'overlay
  //     `baseCosts`, popolato SOLO per i 7 codici comparabili in modalità
  //     Indiretti con assicurazione scelta).
  // showDualPrice = true → la riga mostra entrambi i prezzi con un
  // toggle (radio) che permette di scegliere quale viene effettivamente
  // applicato al calcolo del totale dell'opportunità.
  const _assicCostForCurrent = useMemo(() => {
    if (!trattamento.treatmentCode) return null;
    const code = trattamento.treatmentCode.toUpperCase();
    const t = treatments.find((x) => (x.code || "").toUpperCase() === code);
    return t ? Number(t.cost) : null;
  }, [treatments, trattamento.treatmentCode]);
  const _bddCostForCurrent = _baseCostFor(trattamento.treatmentCode);
  // Mostra la UI dual-price (radio assic./BDD) SOLO quando il caller
  // l'ha esplicitamente abilitata via `enableDualPriceUI`. App.js la
  // attiva per TUTTE le opp Indiretti (create / edit / virtual-dup):
  // per ogni prestazione "Indiretto comparabile" (7 codici ABF/ABO/ABP/
  // ABR/ABT/ABT2/ABTV) l'operatore può scegliere prezzo Assicurazione
  // oppure BDD. Le prestazioni `isBaseOnly` (esistenti solo in BDD)
  // non hanno prezzo assicurazione → restano forzate al BDD (mostrate
  // come testo €X, non come radio).
  // Storico: il 01/06/2026 era stata erroneamente limitata ai soli
  // virtual-dup → regressione segnalata 04/06/2026, ripristinato.
  const showDualPrice =
    !!enableDualPriceUI
    && hasTreatment
    && _assicCostForCurrent !== null
    && _bddCostForCurrent !== null;
  // Selezione corrente del prezzo (Assic. vs BDD). Tracciata
  // ESPLICITAMENTE via `trattamento.priceChoice` per supportare anche
  // il caso in cui i due prezzi siano uguali (richiesta utente
  // 04/06/2026: "se assic. == BDD lasciami comunque scegliere quale
  // imputare per il piano statistico"). Fallback su confronto numerico
  // per record legacy salvati prima dell'introduzione del campo.
  const isUsingBdd = showDualPrice && (
    trattamento.priceChoice === "bdd"
    || (!trattamento.priceChoice && Number(euroValue) === Number(_bddCostForCurrent)
        && Number(_assicCostForCurrent) !== Number(_bddCostForCurrent))
  );
  const _selectAssicPrice = () => {
    if (!showDualPrice) return;
    onUpdate(trattamento.treatmentCode, trattamento.treatmentName, _assicCostForCurrent, "assic");
  };
  const _selectBddPrice = () => {
    if (!showDualPrice) return;
    onUpdate(trattamento.treatmentCode, trattamento.treatmentName, _bddCostForCurrent, "bdd");
  };
  // Edit del prezzo nel dual-price.
  // - side "bdd": edit del prezzo BDD (comportamento storico 04/06/2026).
  // - side "manual": edit del prezzo ASSIC. (Fasdac) sbloccato da
  //   `fasdacPriceOverride` (02/10/2026). Usiamo "manual" così il valore
  //   sopravvive al realign listino (che salta bdd/manual) e non viene
  //   riportato al prezzo assicurativo di listino.
  const _handleDualEdit = (side, raw) => {
    if (!showDualPrice) return;
    const digits = (raw || "").replace(/\D/g, "").slice(0, 5);
    const value = digits === "" ? 0 : Number(digits);
    onUpdate(trattamento.treatmentCode, trattamento.treatmentName, value, side);
  };

  // Req utente 04/06/2026 — campo prezzo editabile in opp Indiretti SOLO
  // se la prestazione è "BDD-only" (esiste solo nel listino privato BDD,
  // non in quello dell'assicurazione). Le prestazioni che vengono dal
  // foglio assicurazione (anche senza controparte BDD) restano readonly:
  // l'operatore non deve poter modificare il prezzo definito
  // dall'assicurazione. Quando la prestazione ha dual-price, l'editabilità
  // è gestita nel blocco radio (BDD editabile, ASSIC. readonly).
  // Implementazione: `isBaseOnly` arriva dal payload `baseExtraTreatments`
  // del backend (vedi routes_treatments.py) ed è propagato al trattamento
  // selezionato dal frontend tramite la lookup nei `treatments`.
  const selectedTreatmentMeta = useMemo(() => {
    if (!trattamento.treatmentCode) return null;
    const codeU = trattamento.treatmentCode.toUpperCase();
    return treatments.find((x) => (x.code || "").toUpperCase() === codeU) || null;
  }, [treatments, trattamento.treatmentCode]);
  const isBaseOnlyTreatment = !!(selectedTreatmentMeta && selectedTreatmentMeta.isBaseOnly);
  const isIndirettiEditable =
    !!enableDualPriceUI && hasTreatment && !showDualPrice && isBaseOnlyTreatment;
  // Flag "Modifica prezzo": il listino marca la prestazione come
  // `price_editable=true` E l'utente (admin/USER con flag `modifica_prezzo`)
  // sta lavorando su una pipeline Assicurazioni. Sblocca l'input € in qualsiasi
  // stage (richiesta utente 10/06/2026).
  const isListinoPriceOverride =
    !!priceOverrideEnabled && hasTreatment && !!(selectedTreatmentMeta && selectedTreatmentMeta.price_editable);
  // Req utente 02/10/2026 — "Modifica prezzo Fasdac": quando
  // l'assicurazione è Fasdac, TUTTE le prestazioni a prezzo singolo
  // diventano editabili (non solo le `price_editable`), sia nelle
  // pipeline Assicurazioni sia Indiretti. Il widget dual-price (7 codici
  // comparabili) resta invariato: lì `showDualPrice` ha precedenza e
  // questo flag non si applica (requisito: "lasciare il doppio prezzo
  // com'è"). L'edit manuale è tracciato come `priceChoice="manual"` →
  // sopravvive al realign e il backend ricalcola nota + totale.
  const isFasdacEditable = !!fasdacPriceOverride && hasTreatment && !showDualPrice;
  const isEditablePrice = isQfoLegacy || isIndirettiEditable || isListinoPriceOverride || isFasdacEditable;

  return (
    <div className="p-3 bg-slate-50 rounded-xl space-y-3 sm:space-y-0">
      {/* Desktop layout */}
      <div className="hidden sm:flex items-center gap-3">
        {enableIndirettoDropdown && (
          <div className="flex-shrink-0">
            <select
              value={trattamento.indirettoTarget || ""}
              onChange={(e) => onSetIndirettoTarget && onSetIndirettoTarget(e.target.value)}
              data-testid={`indiretto-target-${index}`}
              data-bypass-readonly="indiretto-target"
              title="Se valorizzato, al salvataggio questa prestazione viene spostata in una nuova opp Indiretti col primo stage di pipeline"
              className={`h-11 text-xs font-semibold border rounded-lg px-2 focus:outline-none transition-colors ${
                trattamento.indirettoTarget
                  ? "border-amber-400 bg-amber-50 text-amber-700 focus:border-amber-500"
                  : "border-sky-300 bg-sky-50 text-sky-700 focus:border-sky-500"
              }`}
            >
              <option value="">— Sull'opp attuale</option>
              <option value="clinico">→ Indiretti Reparto Clinico</option>
              <option value="ortodonzia">→ Indiretti Ortodonzia</option>
            </select>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-stretch h-11 rounded-lg border border-slate-200 bg-white overflow-hidden">
            {/* Category dropdown */}
            <Popover open={filterOpen} onOpenChange={setFilterOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid={`category-filter-${index}`}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 border-r border-slate-200 transition-colors text-sm rounded-l-lg ${
                    activeCategory ? "bg-blue-50 text-blue-700 font-semibold" : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                  }`}
                  title={activeCategory ? `Filtro: ${activeCategory}` : "Filtra per categoria"}
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span className="capitalize whitespace-nowrap">{activeCategory || "Categoria"}</span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-2 bg-white border-slate-200" align="start">
                <div className="space-y-1">
                  <button type="button" onClick={() => { setActiveCategory(""); setFilterOpen(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCategory ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                    Tutte le categorie
                  </button>
                  {(categories || []).map((cat) => (
                    <button key={cat} type="button" onClick={() => { setActiveCategory(cat); setFilterOpen(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm capitalize transition-colors ${activeCategory === cat ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* Treatment search */}
            <Popover open={searchOpen} onOpenChange={setSearchOpen}>
              <PopoverTrigger asChild>
                <button type="button" role="combobox" className="flex-1 min-w-0 flex items-center px-3 text-left text-sm hover:bg-slate-50 transition-colors overflow-hidden" data-testid={`trattamento-search-${index}`}>
                  {trattamento.treatmentCode ? (
                    <span className="truncate text-slate-800 block w-full">
                      <span className="font-semibold text-blue-600">{trattamento.treatmentCode}</span>
                      <span className="mx-2 text-slate-300">|</span>
                      {trattamento.treatmentName}
                    </span>
                  ) : (
                    <span className="text-slate-400 flex items-center gap-2">
                      <Search className="w-4 h-4" /> Cerca prestazione...
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[450px] p-0 bg-white border-slate-200" align="start">
                <Command shouldFilter={false}>
                  <CommandInput placeholder="Digita codice o nome prestazione..." className="h-11" onValueChange={setSearchQuery} />
                  <CommandList>
                    <CommandEmpty>Nessuna prestazione trovata.</CommandEmpty>
                    <CommandGroup heading={activeCategory ? `Categoria: ${activeCategory}` : undefined}>
                      {sortedTreatments.map((t, tIdx) => {
                        const baseC = _baseCostFor(t.code);
                        return (
                        <CommandItem key={`${t.code}-${tIdx}`} value={`${t.code} ${t.name}`} onSelect={() => { onUpdate(t.code, t.name, t.cost); setSearchOpen(false); setSearchQuery(""); }} className="cursor-pointer py-3">
                          <div className="flex items-center justify-between w-full">
                            <div className="flex-1 min-w-0">
                              <span className="font-bold text-blue-600">{t.code}</span>
                              {t.isBaseOnly && (
                                <span className="ml-1.5 inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" title="Prestazione del listino BDD (non coperta da assicurazione)">
                                  BDD
                                </span>
                              )}
                              <span className="ml-3 text-slate-700">{t.name}</span>
                            </div>
                            {baseC !== null ? (
                              <div className="ml-4 flex flex-col items-end leading-tight" title="Prezzo assicurazione · prezzo BDD">
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Assic.</span>
                                <span className="text-sm font-semibold text-slate-700">&euro;{t.cost}</span>
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 mt-0.5">BDD</span>
                                <span className="text-sm font-semibold text-amber-700">&euro;{baseC}</span>
                              </div>
                            ) : (
                              <span className="ml-4 font-semibold text-slate-600">&euro;{t.cost}</span>
                            )}
                          </div>
                        </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className={`flex-shrink-0 text-center ${showDualPrice ? "w-32" : "w-24"}`}>
          {showDualPrice ? (
            <div
              className="flex flex-col gap-1"
              data-testid={`dual-price-${index}`}
              title="Scegli quale prezzo applicare al totale: assicurazione o BDD"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => { if (isUsingBdd) _selectAssicPrice(); }}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && isUsingBdd) { e.preventDefault(); _selectAssicPrice(); } }}
                data-testid={`dual-price-assic-${index}`}
                className={`flex items-center justify-between px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors cursor-pointer ${
                  !isUsingBdd
                    ? "border-blue-400 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                <span className="flex items-center gap-1">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full border ${!isUsingBdd ? "bg-blue-600 border-blue-600" : "border-slate-300"}`} />
                  ASSIC.
                </span>
                {!isUsingBdd ? (
                  fasdacPriceOverride ? (
                    <span className="flex items-center tabular-nums" onClick={(e) => e.stopPropagation()}>
                      <span>&euro;</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={5}
                        value={euroValue === 0 ? "" : String(euroValue)}
                        onChange={(e) => _handleDualEdit("manual", e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        data-testid={`dual-price-assic-input-${index}`}
                        title="Prezzo Fasdac (modificabile a mano)"
                        className="w-10 ml-0.5 bg-transparent text-right border-b border-blue-300 focus:outline-none focus:border-blue-500 tabular-nums"
                      />
                    </span>
                  ) : (
                    <span className="tabular-nums" data-testid={`dual-price-assic-readonly-${index}`}>&euro;{euroValue}</span>
                  )
                ) : (
                  <span className="tabular-nums">&euro;{_assicCostForCurrent}</span>
                )}
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={() => { if (!isUsingBdd) _selectBddPrice(); }}
                onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isUsingBdd) { e.preventDefault(); _selectBddPrice(); } }}
                data-testid={`dual-price-bdd-${index}`}
                className={`flex items-center justify-between px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors cursor-pointer ${
                  isUsingBdd
                    ? "border-amber-400 bg-amber-50 text-amber-700"
                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                }`}
              >
                <span className="flex items-center gap-1">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full border ${isUsingBdd ? "bg-amber-600 border-amber-600" : "border-slate-300"}`} />
                  BDD
                </span>
                {isUsingBdd ? (
                  <span className="flex items-center tabular-nums">
                    <span>&euro;</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={5}
                      value={euroValue === 0 ? "" : String(euroValue)}
                      onChange={(e) => _handleDualEdit("bdd", e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.target.select()}
                      placeholder="0"
                      data-testid={`dual-price-bdd-input-${index}`}
                      title="Prezzo BDD (editabile, pre-compilato dal listino BDD)"
                      className="w-10 ml-0.5 bg-transparent text-right border-b border-amber-300 focus:outline-none focus:border-amber-500 tabular-nums"
                    />
                  </span>
                ) : (
                  <span className="tabular-nums">&euro;{_bddCostForCurrent}</span>
                )}
              </div>
            </div>
          ) : hasTreatment && isEditablePrice ? (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={isQfoLegacy ? 4 : 5}
              value={euroValue === 0 ? "" : String(euroValue)}
              onChange={(e) => handlePriceChange(e.target.value)}
              placeholder="0"
              data-testid={isQfoLegacy ? `qfo-price-${index}` : (isFasdacEditable ? `fasdac-price-${index}` : `indiretti-price-${index}`)}
              title={isQfoLegacy
                ? "Quota annuale Fasdac (editabile, max 4 cifre)"
                : (isFasdacEditable
                    ? "Prezzo Fasdac (modificabile a mano)"
                    : "Prezzo Indiretti (editabile, pre-compilato dal listino)")}
              className="w-20 mx-auto h-9 text-center text-sm font-semibold border-2 border-amber-300 bg-amber-50 rounded-md focus:outline-none focus:border-amber-500"
            />
          ) : (
            <span className="text-sm font-medium text-slate-500">{hasTreatment ? `\u20ac${euroValue}` : "-"}</span>
          )}
        </div>
        <div className="w-24 flex-shrink-0 text-center">
          <span className={`text-sm font-medium ${hasTreatment ? "text-slate-600" : "text-slate-400"}`}>
            {hasTreatment ? (qtyLabel === "Unitario" ? "x1" : qtyLabel) : "-"}
          </span>
        </div>
        <div className="w-24 flex-shrink-0">
          <div className={`h-11 flex items-center justify-center rounded-lg font-bold text-sm ${hasTreatment ? "bg-[#f8dfd6] text-[#1a1fa8] border border-[#f0c5b6]" : "bg-slate-100 text-slate-400"}`}>
            {hasTreatment ? `\u20ac${totalEuro}` : "-"}
          </div>
        </div>
        <div className="w-10 flex-shrink-0">
          {showRemove && (
            <button type="button" onClick={onRemove} className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile layout */}
      <div className="sm:hidden space-y-3">
        {enableIndirettoDropdown && (
          <select
            value={trattamento.indirettoTarget || ""}
            onChange={(e) => onSetIndirettoTarget && onSetIndirettoTarget(e.target.value)}
            data-testid={`indiretto-target-m-${index}`}
            data-bypass-readonly="indiretto-target"
            className={`w-full h-10 text-xs font-semibold border rounded-lg px-2 focus:outline-none transition-colors ${
              trattamento.indirettoTarget
                ? "border-amber-400 bg-amber-50 text-amber-700 focus:border-amber-500"
                : "border-sky-300 bg-sky-50 text-sky-700 focus:border-sky-500"
            }`}
          >
            <option value="">— Sull'opp attuale</option>
            <option value="clinico">→ Indiretti Reparto Clinico</option>
            <option value="ortodonzia">→ Indiretti Ortodonzia</option>
          </select>
        )}
        <div className="flex items-stretch h-11 rounded-lg border border-slate-200 bg-white">
          <Popover open={filterOpenMobile} onOpenChange={setFilterOpenMobile}>
            <PopoverTrigger asChild>
              <button type="button" data-testid={`category-filter-m-${index}`} className={`flex-shrink-0 flex items-center gap-1.5 px-3 border-r border-slate-200 transition-colors text-sm ${activeCategory ? "bg-blue-50 text-blue-700 font-semibold" : "bg-slate-50 text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}>
                <Filter className="w-3.5 h-3.5" />
                <span className="capitalize whitespace-nowrap text-xs">{activeCategory || "Cat."}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-52 p-2 bg-white border-slate-200" align="start">
              <div className="space-y-1">
                <button type="button" onClick={() => { setActiveCategory(""); setFilterOpenMobile(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!activeCategory ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>Tutte le categorie</button>
                {(categories || []).map((cat) => (
                  <button key={cat} type="button" onClick={() => { setActiveCategory(cat); setFilterOpenMobile(false); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm capitalize transition-colors ${activeCategory === cat ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50"}`}>{cat}</button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Popover open={searchOpenMobile} onOpenChange={setSearchOpenMobile}>
            <PopoverTrigger asChild>
              <button type="button" role="combobox" className="flex-1 flex items-center px-3 text-left text-sm hover:bg-slate-50 transition-colors min-w-0" data-testid={`trattamento-search-m-${index}`}>
                {trattamento.treatmentCode ? (
                  <span className="truncate text-slate-800 text-xs">
                    <span className="font-semibold text-blue-600">{trattamento.treatmentCode}</span>
                    <span className="mx-1 text-slate-300">|</span>
                    {trattamento.treatmentName}
                  </span>
                ) : (
                  <span className="text-slate-400 flex items-center gap-1 text-xs"><Search className="w-3.5 h-3.5" /> Cerca prestazione...</span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[min(450px,90vw)] p-0 bg-white border-slate-200" align="start">
              <Command shouldFilter={false}>
                <CommandInput placeholder="Digita codice o nome..." className="h-11" onValueChange={setSearchQueryMobile} />
                <CommandList>
                  <CommandEmpty>Nessuna prestazione trovata.</CommandEmpty>
                  <CommandGroup heading={activeCategory ? `Categoria: ${activeCategory}` : undefined}>
                    {sortedTreatmentsMobile.map((t, tIdx) => {
                      const baseC = _baseCostFor(t.code);
                      return (
                      <CommandItem key={`${t.code}-${tIdx}`} value={`${t.code} ${t.name}`} onSelect={() => { onUpdate(t.code, t.name, t.cost); setSearchOpenMobile(false); setSearchQueryMobile(""); }} className="cursor-pointer py-3">
                        <div className="flex items-center justify-between w-full">
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-blue-600">{t.code}</span>
                            {t.isBaseOnly && (
                              <span className="ml-1 inline-block text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" title="Prestazione del listino BDD">
                                BDD
                              </span>
                            )}
                            <span className="ml-1 text-slate-700 text-xs">{t.name}</span>
                          </div>
                          {baseC !== null ? (
                            <div className="ml-2 flex flex-col items-end leading-tight" title="Prezzo assicurazione · prezzo BDD">
                              <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-400">Assic.</span>
                              <span className="text-xs font-semibold text-slate-700">&euro;{t.cost}</span>
                              <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-600 mt-0.5">BDD</span>
                              <span className="text-xs font-semibold text-amber-700">&euro;{baseC}</span>
                            </div>
                          ) : (
                            <span className="ml-2 font-semibold text-slate-600 text-xs">&euro;{t.cost}</span>
                          )}
                        </div>
                      </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`text-center ${showDualPrice ? "w-32" : "w-24"}`}>
              <span className="text-[10px] text-slate-400 block sm:hidden">Prezzo</span>
              {showDualPrice ? (
                <div
                  className="flex flex-col gap-1"
                  data-testid={`dual-price-m-${index}`}
                  title="Scegli quale prezzo applicare al totale: assicurazione o BDD"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (isUsingBdd) _selectAssicPrice(); }}
                    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && isUsingBdd) { e.preventDefault(); _selectAssicPrice(); } }}
                    data-testid={`dual-price-assic-m-${index}`}
                    className={`flex items-center justify-between px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors cursor-pointer ${
                      !isUsingBdd
                        ? "border-blue-400 bg-blue-50 text-blue-700"
                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full border ${!isUsingBdd ? "bg-blue-600 border-blue-600" : "border-slate-300"}`} />
                      ASSIC.
                    </span>
                    {!isUsingBdd ? (
                      fasdacPriceOverride ? (
                        <span className="flex items-center tabular-nums" onClick={(e) => e.stopPropagation()}>
                          <span>&euro;</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={5}
                            value={euroValue === 0 ? "" : String(euroValue)}
                            onChange={(e) => _handleDualEdit("manual", e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.target.select()}
                            placeholder="0"
                            data-testid={`dual-price-assic-input-m-${index}`}
                            title="Prezzo Fasdac (modificabile a mano)"
                            className="w-10 ml-0.5 bg-transparent text-right border-b border-blue-300 focus:outline-none focus:border-blue-500 tabular-nums"
                          />
                        </span>
                      ) : (
                        <span className="tabular-nums" data-testid={`dual-price-assic-readonly-m-${index}`}>&euro;{euroValue}</span>
                      )
                    ) : (
                      <span className="tabular-nums">&euro;{_assicCostForCurrent}</span>
                    )}
                  </div>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => { if (!isUsingBdd) _selectBddPrice(); }}
                    onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !isUsingBdd) { e.preventDefault(); _selectBddPrice(); } }}
                    data-testid={`dual-price-bdd-m-${index}`}
                    className={`flex items-center justify-between px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors cursor-pointer ${
                      isUsingBdd
                        ? "border-amber-400 bg-amber-50 text-amber-700"
                        : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <span className="flex items-center gap-1">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full border ${isUsingBdd ? "bg-amber-600 border-amber-600" : "border-slate-300"}`} />
                      BDD
                    </span>
                    {isUsingBdd ? (
                      <span className="flex items-center tabular-nums">
                        <span>&euro;</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={5}
                          value={euroValue === 0 ? "" : String(euroValue)}
                          onChange={(e) => _handleDualEdit("bdd", e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          placeholder="0"
                          data-testid={`dual-price-bdd-input-m-${index}`}
                          title="Prezzo BDD (editabile, pre-compilato dal listino BDD)"
                          className="w-10 ml-0.5 bg-transparent text-right border-b border-amber-300 focus:outline-none focus:border-amber-500 tabular-nums"
                        />
                      </span>
                    ) : (
                      <span className="tabular-nums">&euro;{_bddCostForCurrent}</span>
                    )}
                  </div>
                </div>
              ) : hasTreatment && isEditablePrice ? (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={isQfoLegacy ? 4 : 5}
                  value={euroValue === 0 ? "" : String(euroValue)}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  placeholder="0"
                  data-testid={isQfoLegacy ? `qfo-price-m-${index}` : (isFasdacEditable ? `fasdac-price-m-${index}` : `indiretti-price-m-${index}`)}
                  title={isQfoLegacy
                    ? "Quota annuale Fasdac (editabile, max 4 cifre)"
                    : "Prezzo Indiretti (editabile, pre-compilato dal listino)"}
                  className="w-20 mx-auto h-9 text-center text-sm font-semibold border-2 border-amber-300 bg-amber-50 rounded-md focus:outline-none focus:border-amber-500"
                />
              ) : (
                <span className="text-sm font-medium text-slate-500">{hasTreatment ? `\u20ac${euroValue}` : "-"}</span>
              )}
            </div>
            <div className="text-center w-24">
              <span className="text-[10px] text-slate-400 block sm:hidden">Qty</span>
              <span className={`text-sm font-medium ${trattamento.euro ? "text-slate-600" : "text-slate-400"}`}>{hasTreatment ? (qtyLabel === "Unitario" ? "x1" : qtyLabel) : "-"}</span>
            </div>
            <div className="text-center w-24">
              <span className="text-[10px] text-slate-400 block sm:hidden">Totale</span>
              <span className={`text-sm font-bold ${hasTreatment ? "text-[#1a1fa8]" : "text-slate-400"}`}>{hasTreatment ? `\u20ac${totalEuro}` : "-"}</span>
            </div>
          </div>
          {showRemove && (
            <button type="button" onClick={onRemove} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Compact teeth display helper ----

const getCompactTeethDisplay = (denti) => {
  if (!denti || denti.length === 0) return [];
  const s = new Set(denti);
  const result = [];
  const accounted = new Set();

  const fullUp = UPPER_TEETH.every((t) => s.has(t));
  const fullLo = LOWER_TEETH.every((t) => s.has(t));

  if (fullUp && fullLo) {
    return [{ label: "Tutta la bocca", teeth: [...denti] }];
  }
  if (fullUp) {
    const teeth = [...UPPER_TEETH];
    [...UPPER_RIGHT_BABY_IDS, ...UPPER_LEFT_BABY_IDS].forEach((t) => {
      if (s.has(t)) { teeth.push(t); accounted.add(t); }
    });
    result.push({ label: "Arcata Superiore", teeth });
    UPPER_TEETH.forEach((t) => accounted.add(t));
  }
  if (fullLo) {
    const teeth = [...LOWER_TEETH];
    [...LOWER_LEFT_BABY_IDS, ...LOWER_RIGHT_BABY_IDS].forEach((t) => {
      if (s.has(t)) { teeth.push(t); accounted.add(t); }
    });
    result.push({ label: "Arcata Inferiore", teeth });
    LOWER_TEETH.forEach((t) => accounted.add(t));
  }

  const semiDefs = [
    { teeth: UPPER_RIGHT, baby: UPPER_RIGHT_BABY_IDS, label: "Emiarcata Sup. Dx" },
    { teeth: UPPER_LEFT, baby: UPPER_LEFT_BABY_IDS, label: "Emiarcata Sup. Sx" },
    { teeth: LOWER_LEFT, baby: LOWER_LEFT_BABY_IDS, label: "Emiarcata Inf. Sx" },
    { teeth: LOWER_RIGHT, baby: LOWER_RIGHT_BABY_IDS, label: "Emiarcata Inf. Dx" },
  ];
  for (const semi of semiDefs) {
    if (semi.teeth.every((t) => s.has(t) && !accounted.has(t))) {
      const teethGroup = [...semi.teeth];
      semi.baby.forEach((t) => { if (s.has(t)) { teethGroup.push(t); accounted.add(t); } });
      result.push({ label: semi.label, teeth: teethGroup });
      semi.teeth.forEach((t) => accounted.add(t));
    }
  }

  [...s].filter((t) => !accounted.has(t)).sort((a, b) => +a - +b)
    .forEach((t) => result.push({ label: t, teeth: [t] }));

  return result;
};

// ---- Treatment Group Card ----

const TreatmentGroupCard = ({ group, groupIdx: _groupIdx, treatments, categories, baseCosts, enableDualPriceUI, enableIndirettoDropdown, priceOverrideEnabled, fasdacPriceOverride, onToothClick, onAddTrattamento, onRemoveTrattamento, onUpdateTrattamento, onSetIndirettoTarget, onRemoveGroup: _onRemoveGroup }) => {
  const multiplier = group.fullMouth ? 1 : getEffectiveMultiplier(group.denti);
  const arcataInfo = group.fullMouth ? { label: "Tutta la bocca", qtyLabel: "Unitario" } : getArcataInfo(group.denti);
  const compactTeeth = useMemo(() => getCompactTeethDisplay(group.denti), [group.denti]);

  return (
    <div className="bg-white rounded-2xl p-3 sm:p-6 border border-slate-200 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-3">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <img
            src="/bdd-icon.svg"
            alt="BDD"
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex-shrink-0"
          />
          <span className="text-xs sm:text-sm font-bold text-slate-700 uppercase tracking-wide">PRESTAZIONI SU:</span>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            {compactTeeth.length > 0 ? (
              compactTeeth.map((item) => {
                const isRange = item.teeth.length > 1;
                return (
                  <div
                    key={item.label}
                    className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-lg border-2 text-xs sm:text-sm font-bold cursor-pointer transition-colors ${
                      isRange
                        ? "border-blue-400 bg-blue-50 text-blue-600 hover:border-red-400 hover:text-red-500 hover:bg-red-50"
                        : "border-blue-500 bg-white text-blue-600 hover:border-red-500 hover:text-red-500"
                    }`}
                    onClick={() => item.teeth.forEach((t) => onToothClick(t))}
                    title={isRange ? `Rimuovi ${item.label} (${item.teeth.length} denti)` : `Rimuovi dente ${item.label}`}
                  >
                    {item.label}
                  </div>
                );
              })
            ) : (
              <span className="text-xs sm:text-sm text-slate-400 italic">Seleziona denti</span>
            )}
          </div>
        </div>
      </div>

      {/* Treatments table */}
      <div className="space-y-3">
        <div className="hidden sm:flex items-center text-xs text-slate-400 font-semibold uppercase tracking-wide gap-3 px-3">
          <span className="flex-1 min-w-0">Prestazione</span>
          <span className="w-24 text-center">Unitario</span>
          <span className="w-24 text-center">Qty</span>
          <span className="w-24 text-center">Totale</span>
          <span className="w-10" />
        </div>
        {group.trattamenti.map((tratt, idx) => (
          <TrattamentoRow
            key={tratt.id}
            trattamento={tratt}
            treatments={treatments}
            categories={categories}
            baseCosts={baseCosts}
            enableDualPriceUI={enableDualPriceUI || !!tratt.indirettoTarget}
            enableIndirettoDropdown={enableIndirettoDropdown}
            priceOverrideEnabled={priceOverrideEnabled}
            fasdacPriceOverride={fasdacPriceOverride}
            index={idx}
            showRemove={true}
            numDenti={multiplier}
            qtyLabel={arcataInfo.qtyLabel}
            onRemove={() => onRemoveTrattamento(group.id, tratt.id)}
            onUpdate={(code, name, cost, priceChoice) => onUpdateTrattamento(group.id, tratt.id, code, name, cost, priceChoice)}
            onSetIndirettoTarget={(target) => onSetIndirettoTarget && onSetIndirettoTarget(group.id, tratt.id, target)}
          />
        ))}
        <button type="button" onClick={() => onAddTrattamento(group.id)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium mt-2">
          <Plus className="w-4 h-4" /> Aggiungi prestazione
        </button>

        {/* Group total — aligned to the Totale column */}
        {group.trattamenti.filter((t) => t.treatmentCode).length > 1 && (
          <div className="hidden sm:flex items-center gap-3 mt-3 pt-3 px-3 border-t border-slate-200">
            <div className="flex-1 min-w-0" />
            <div className="w-24 flex-shrink-0" />
            <div className="w-24 flex-shrink-0" />
            <div className="w-24 flex-shrink-0 text-center">
              <span className="text-lg font-bold text-[#1a1fa8]">
                €{group.trattamenti.reduce((sum, t) => sum + (t.treatmentCode ? (t.euro || 0) * multiplier : 0), 0)}
              </span>
            </div>
            <div className="w-10 flex-shrink-0" />
          </div>
        )}
        {/* Mobile group total */}
        {group.trattamenti.filter((t) => t.treatmentCode).length > 1 && (
          <div className="sm:hidden flex justify-end items-center pt-3 mt-3 border-t border-slate-200">
            <span className="text-lg font-bold text-[#1a1fa8]">
              €{group.trattamenti.reduce((sum, t) => sum + (t.treatmentCode ? (t.euro || 0) * multiplier : 0), 0)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Main Odontogram component ----

const Odontogram = ({ pianoCura, setPianoCura, treatments, categories, baseCosts, enableDualPriceUI, enableIndirettoDropdown, priceOverrideEnabled = false, fasdacPriceOverride = false }) => {
  const _allSelectedTeeth = pianoCura.flatMap((p) => p.denti || []);
  const _teethWithTrattamenti = pianoCura.filter((p) => p.trattamenti.some((t) => t.treatmentCode)).flatMap((p) => p.denti || []);
  // Build treatment count per tooth: count how many treatments are assigned to each tooth
  const teethTreatmentCounts = {};
  pianoCura.forEach((p) => {
    const treatmentCount = p.trattamenti.filter((t) => t.treatmentCode).length;
    if (treatmentCount > 0) {
      (p.denti || []).forEach((d) => {
        teethTreatmentCounts[d] = (teethTreatmentCounts[d] || 0) + treatmentCount;
      });
    }
  });
  // Locked = teeth in groups that already have a treatment assigned (shown as light blue)
  const lockedTeeth = pianoCura.filter((p) => p.trattamenti.some((t) => t.treatmentCode)).flatMap((p) => p.denti || []);
  // Current selection = teeth in the active group (last group without treatments, or all if no such group)
  const activeGroup = pianoCura.length > 0 && !pianoCura[pianoCura.length - 1].trattamenti.some((t) => t.treatmentCode)
    ? pianoCura[pianoCura.length - 1]
    : null;
  const currentSelectedTeeth = activeGroup ? (activeGroup.denti || []) : [];

  // Arch checkbox state based on ACTIVE group only (allows overlapping with locked groups)
  const activeGroupDentiSet = useMemo(() => new Set(activeGroup ? activeGroup.denti : []), [activeGroup]);
  const isUpperArcataSelected = UPPER_TEETH.every((t) => activeGroupDentiSet.has(t));
  const isLowerArcataSelected = LOWER_TEETH.every((t) => activeGroupDentiSet.has(t));
  const hasUpperTeethSelected = UPPER_TEETH.some((t) => activeGroupDentiSet.has(t));
  const hasLowerTeethSelected = LOWER_TEETH.some((t) => activeGroupDentiSet.has(t));

  const nextGroupId = () => Math.max(...pianoCura.map((p) => p.id), 0) + 1;
  const emptyTrattamento = () => ({ id: 1, treatmentCode: "", treatmentName: "", euro: 0 });
  const lastGroupHasTreatments = () => pianoCura.length > 0 && pianoCura[pianoCura.length - 1].trattamenti.some((t) => t.treatmentCode);

  // --- handlers ---

  const handleArcataToggle = (arcataTeeth, hasTeeth) => {
    if (hasTeeth) {
      // Remove teeth only from the active group, leave locked groups untouched
      if (!activeGroup) return;
      const remaining = activeGroup.denti.filter((d) => !arcataTeeth.includes(d));
      if (remaining.length === 0) {
        setPianoCura(pianoCura.filter((p) => p.id !== activeGroup.id));
      } else {
        setPianoCura(pianoCura.map((p) => p.id === activeGroup.id ? { ...p, denti: remaining } : p));
      }
    } else {
      // Add teeth — filter only against active group (allow overlap with locked groups)
      const toAdd = arcataTeeth.filter((t) => !activeGroupDentiSet.has(t));
      if (toAdd.length === 0) return;
      if (pianoCura.length === 0 || lastGroupHasTreatments()) {
        setPianoCura([...pianoCura, { id: nextGroupId(), denti: toAdd.sort((a, b) => +a - +b), trattamenti: [emptyTrattamento()] }]);
      } else {
        setPianoCura(pianoCura.map((p, i) => (i === pianoCura.length - 1 ? { ...p, denti: [...p.denti, ...toAdd].sort((a, b) => +a - +b) } : p)));
      }
    }
  };

  const handleToothClick = (num) => {
    // Check active group first (prioritize over locked groups for toggle)
    if (activeGroup && activeGroup.denti.includes(num)) {
      const remaining = activeGroup.denti.filter((d) => d !== num);
      setPianoCura(remaining.length === 0 ? pianoCura.filter((p) => p.id !== activeGroup.id) : pianoCura.map((p) => (p.id === activeGroup.id ? { ...p, denti: remaining } : p)));
      return;
    }

    // Otherwise → add to current active group or create new one (even if tooth is in a locked group)
    if (pianoCura.length === 0 || lastGroupHasTreatments()) {
      setPianoCura([...pianoCura, { id: nextGroupId(), denti: [num], trattamenti: [emptyTrattamento()] }]);
    } else {
      setPianoCura(pianoCura.map((p, i) => (i === pianoCura.length - 1 ? { ...p, denti: [...new Set([...p.denti, num])].sort((a, b) => +a - +b) } : p)));
    }
  };

  const addTrattamento = (gid) => {
    setPianoCura(pianoCura.map((p) => {
      if (p.id !== gid) return p;
      const newId = Math.max(...p.trattamenti.map((t) => t.id), 0) + 1;
      return { ...p, trattamenti: [...p.trattamenti, { id: newId, treatmentCode: "", treatmentName: "", euro: 0 }] };
    }));
  };

  const removeTrattamento = (gid, tid) => {
    setPianoCura((prev) => {
      const target = prev.find((p) => p.id === gid);
      // Last treatment of the group → remove the whole group
      if (target && target.trattamenti.length === 1) {
        return prev.filter((p) => p.id !== gid);
      }
      return prev.map((p) =>
        p.id === gid ? { ...p, trattamenti: p.trattamenti.filter((t) => t.id !== tid) } : p
      );
    });
  };

  const updateTrattamento = (gid, tid, code, name, cost, priceChoice) => {
    setPianoCura(pianoCura.map((p) => (p.id === gid ? {
      ...p,
      trattamenti: p.trattamenti.map((t) => {
        if (t.id !== tid) return t;
        const next = { ...t, treatmentCode: code, treatmentName: name, euro: cost };
        // Aggiorniamo `priceChoice` solo se il caller lo specifica
        // esplicitamente. Quando il codice prestazione CAMBIA (cioè
        // l'utente sceglie un altro trattamento dal dropdown), resettiamo
        // il priceChoice a "" così la logica di default torna a inferire
        // dal listino corrente.
        if (priceChoice !== undefined) {
          next.priceChoice = priceChoice;
        } else if (code !== t.treatmentCode) {
          next.priceChoice = "";
        }
        return next;
      }),
    } : p)));
  };

  // Cambia il routing "Indiretto" per una singola riga prestazione.
  // Richiesta utente 04/06/2026 (rev): la prestazione DEVE rimanere
  // invariata; cambia SOLO il prezzo coerentemente col nuovo listino:
  //   - target "" (torna su opp originale) → priceChoice "" così il
  //     realign listino assicurativo dell'opp ripristina il prezzo.
  //   - target Indiretti con codice presente sia in foglio assicurazione
  //     sia in BDD (`baseCosts[code]` valorizzato) → priceChoice "" per
  //     usare il dual-price di default (ASSIC.). Le radio Assic./BDD
  //     compariranno automaticamente (enableDualPriceUI è attivo per
  //     indirettoTarget != ""). Storico: lista hard-coded di 7 codici
  //     fino al 04/06/2026, poi estesa lato backend a TUTTI i codici
  //     comuni assic+BDD.
  //   - target Indiretti su codice presente SOLO in BDD (`baseCosts`
  //     contiene il codice ma il listino assicurazione no) → no dual-
  //     price possibile, setto priceChoice "bdd" ed euro a baseCosts.
  //   - target Indiretti su codice senza match BDD → priceChoice "manual",
  //     euro invariato, input editabile.
  const setIndirettoTarget = (gid, tid, target) => {
    const nextTarget = (target || "").trim().toLowerCase();
    setPianoCura(pianoCura.map((p) => (p.id === gid ? {
      ...p,
      trattamenti: p.trattamenti.map((t) => {
        if (t.id !== tid) return t;
        if ((t.indirettoTarget || "") === nextTarget) return t;
        if (!t.treatmentCode) {
          return { ...t, indirettoTarget: nextTarget };
        }
        if (!nextTarget) {
          return { ...t, indirettoTarget: "", priceChoice: "" };
        }
        const codeU = String(t.treatmentCode).toUpperCase();
        const bddCost = baseCosts && baseCosts[codeU];
        const hasBdd = typeof bddCost === "number" && bddCost > 0;
        const ins = (treatments || []).find((x) => (x.code || "").toUpperCase() === codeU);
        const insCost = ins ? Number(ins.cost) : 0;
        const hasInsurance = insCost > 0;
        if (hasBdd && hasInsurance) {
          // Doppio prezzo disponibile: default Assic, l'utente può
          // swappare con la radio "BDD".
          return { ...t, indirettoTarget: nextTarget, priceChoice: "" };
        }
        if (hasBdd) {
          // Solo prezzo BDD disponibile.
          return { ...t, indirettoTarget: nextTarget, euro: bddCost, priceChoice: "bdd" };
        }
        // Nessun prezzo BDD nel listino: mantieni euro corrente,
        // input editabile.
        return { ...t, indirettoTarget: nextTarget, priceChoice: "manual" };
      }),
    } : p)));
  };

  const removeGroup = (gid) => setPianoCura(pianoCura.filter((p) => p.id !== gid));

  const _addNewGroup = () => {
    setPianoCura([...pianoCura, { id: nextGroupId(), denti: [], trattamenti: [emptyTrattamento()] }]);
  };

  // "Tutta la bocca" checkbox state: true if any group has fullMouth flag
  const isTuttaLaBocca = pianoCura.some((p) => p.fullMouth);

  const _handleTuttaLaBoccaToggle = () => {
    if (isTuttaLaBocca) {
      // Uncheck: remove the fullMouth group
      setPianoCura(pianoCura.filter((p) => !p.fullMouth));
    } else {
      const allTeethSorted = [...ALL_TEETH_WITH_BABY].sort((a, b) => +a - +b);
      if (pianoCura.length === 0 || lastGroupHasTreatments()) {
        setPianoCura([...pianoCura, { id: nextGroupId(), denti: allTeethSorted, trattamenti: [emptyTrattamento()], fullMouth: true }]);
      } else {
        setPianoCura(pianoCura.map((p, i) => (i === pianoCura.length - 1 ? { ...p, denti: allTeethSorted, fullMouth: true } : p)));
      }
    }
  };

  const handleQuickTreatment = (code) => {
    const t = treatments.find((tr) => tr.code === code);
    if (!t) return;
    let fmGroup = pianoCura.find((p) => p.fullMouth);

    // If no fullMouth group exists, create one with all teeth (incl. decidui)
    if (!fmGroup) {
      const allTeethSorted = [...ALL_TEETH_WITH_BABY].sort((a, b) => +a - +b);
      const newGroup = { id: nextGroupId(), denti: allTeethSorted, trattamenti: [{ id: 1, treatmentCode: t.code, treatmentName: t.name, euro: t.cost }], fullMouth: true };
      setPianoCura([...pianoCura, newGroup]);
      return;
    }

    // Toggle: if already selected, remove it
    if (fmGroup.trattamenti.some((tr) => tr.treatmentCode === code)) {
      setPianoCura(pianoCura.map((p) => {
        if (p.id !== fmGroup.id) return p;
        const filtered = p.trattamenti.filter((tr) => tr.treatmentCode !== code);
        // If no treatments left, remove the whole group
        if (filtered.length === 0 || !filtered.some((tr) => tr.treatmentCode)) {
          return null;
        }
        return { ...p, trattamenti: filtered };
      }).filter(Boolean));
      return;
    }

    // Add treatment
    setPianoCura(pianoCura.map((p) => {
      if (p.id !== fmGroup.id) return p;
      const emptyIdx = p.trattamenti.findIndex((tr) => !tr.treatmentCode);
      if (emptyIdx >= 0) {
        return { ...p, trattamenti: p.trattamenti.map((tr, i) => i === emptyIdx ? { ...tr, treatmentCode: t.code, treatmentName: t.name, euro: t.cost } : tr) };
      }
      const newId = Math.max(...p.trattamenti.map((tr) => tr.id), 0) + 1;
      return { ...p, trattamenti: [...p.trattamenti, { id: newId, treatmentCode: t.code, treatmentName: t.name, euro: t.cost }] };
    }));
  };

  // Track which quick codes are already selected in the fullMouth group
  const selectedQuickCodes = useMemo(() => {
    const fmGroup = pianoCura.find((p) => p.fullMouth);
    if (!fmGroup) return new Set();
    return new Set(fmGroup.trattamenti.filter((t) => t.treatmentCode).map((t) => t.treatmentCode));
  }, [pianoCura]);

  return (
    <div className="space-y-6">
      <DentalChart
        allSelectedTeeth={currentSelectedTeeth}
        lockedTeeth={lockedTeeth}
        teethTreatmentCounts={teethTreatmentCounts}
        onToothClick={handleToothClick}
        isUpperArcataSelected={isUpperArcataSelected}
        isLowerArcataSelected={isLowerArcataSelected}
        hasUpperTeethSelected={hasUpperTeethSelected}
        hasLowerTeethSelected={hasLowerTeethSelected}
        onArcataToggle={handleArcataToggle}
        treatments={treatments}
        onQuickTreatment={handleQuickTreatment}
        selectedQuickCodes={selectedQuickCodes}
      />

      {pianoCura.length > 0 && (
        <div className="space-y-4">
          {[...pianoCura].reverse().map((group, idx) => (
            <TreatmentGroupCard
              key={group.id}
              group={group}
              groupIdx={pianoCura.length - 1 - idx}
              treatments={treatments}
              categories={categories}
              baseCosts={baseCosts}
              enableDualPriceUI={enableDualPriceUI}
              enableIndirettoDropdown={enableIndirettoDropdown}
              priceOverrideEnabled={priceOverrideEnabled}
              fasdacPriceOverride={fasdacPriceOverride}
              onToothClick={handleToothClick}
              onAddTrattamento={addTrattamento}
              onRemoveTrattamento={removeTrattamento}
              onUpdateTrattamento={updateTrattamento}
              onSetIndirettoTarget={setIndirettoTarget}
              onRemoveGroup={removeGroup}
            />
          ))}
        </div>
      )}

      {pianoCura.length === 0 && (
        <div className="text-center py-6" />
      )}
    </div>
  );
};

export default Odontogram;
